import { ChangeEvent, type JSX } from 'react'
import { FormHelperText, InputAdornment, TextField, TextFieldProps, useTheme } from '@mui/material'
import { Field, FieldProps } from 'formik'

import useStyles from './index.styles'

type IProps = {
  name: string // formik field name
  label?: string
  type?: string
  placeholder?: string
  disabled?: boolean
  hideErrors?: boolean
  startAdornment?: JSX.Element | undefined // startAdornment component, if it's needed to be displayed in input - usually icon or a symbol at the beginning of an input field
  endAdornment?: JSX.Element | undefined // endAdornment component, if it's needed to be displayed in input - usually icon or a symbol at the end of an input field
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  onBlur?: () => void
} & TextFieldProps

const FormikTextField = (props: IProps) => {
  const { name, label, type, placeholder, disabled, startAdornment, endAdornment, size, variant, hideErrors = false, onChange, onBlur, margin, ...rest } = props

  const classes = useStyles
  const theme = useTheme()

  return (
    <Field name={name}>
      {({ field, form }: FieldProps) => {
        const { errors, touched } = form
        const hasError = Boolean(errors[name] && touched[name])

        return (
          <>
            <TextField
              fullWidth={true}
              {...rest}
              {...field}
              onChange={(changeProps) => {
                onChange?.(changeProps)
                field.onChange(changeProps)
              }}
              onBlur={(blurProps) => {
                onBlur?.()
                field.onBlur(blurProps)
              }}
              error={hasError}
              slotProps={{
                htmlInput: {
                  id: `${name}-textfield`,
                  'data-testid': `${name}-textfield`,
                  'aria-labelledby': `${name}-textfield-label`,
                  'aria-label': label,
                  step: 1,
                  ...props.slotProps?.htmlInput,
                },
                input: {
                  startAdornment: startAdornment ? <InputAdornment position="start">{startAdornment}</InputAdornment> : undefined,
                  endAdornment: endAdornment ? <InputAdornment position="end">{endAdornment}</InputAdornment> : undefined,
                  ...props.slotProps?.input,
                },
                inputLabel: {
                  htmlFor: `${name}-textfield`,
                  id: `${name}-textfield-label`,
                  ...props.slotProps?.inputLabel,
                },
              }}
              sx={{
                ...classes.input(theme, disabled),
                ...props.sx,
              }}
              label={label}
              margin={margin ?? 'normal'}
              size={size ?? 'medium'}
              variant={variant ?? 'outlined'}
              type={type ?? 'text'}
              disabled={disabled}
              placeholder={placeholder ?? undefined}
            >
              {props.children}
            </TextField>

            {!hideErrors && hasError && (
              <FormHelperText sx={classes.errorText} data-testid={`${name}-error-text`}>
                {errors[name] as string}
              </FormHelperText>
            )}
          </>
        )
      }}
    </Field>
  )
}

export default FormikTextField
